import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const RegistrationForm = () => {
  console.log('🔄 RegistrationForm renderizando...');
  
  const navigate = useNavigate();
  const { register, loading: authLoading, error: authError } = useAuth();
  
  console.log('✅ useAuth executado:', { register: !!register, authLoading, authError });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
    agreePrivacy: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = (password) => {
    const checks = {
      length: password?.length >= 8,
      uppercase: /[A-Z]/?.test(password),
      lowercase: /[a-z]/?.test(password),
      number: /\d/?.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/?.test(password)
    };
    
    const score = Object.values(checks)?.filter(Boolean)?.length;
    return { checks, score };
  };

  const getPasswordStrength = (password) => {
    const { score } = validatePassword(password);
    if (score <= 2) return { label: 'Fraca', color: 'bg-error', width: '33%' };
    if (score <= 3) return { label: 'Média', color: 'bg-warning', width: '66%' };
    return { label: 'Forte', color: 'bg-success', width: '100%' };
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'Nome é obrigatório';
    }
    
    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Sobrenome é obrigatório';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email inválido';
    }
    
    return newErrors;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData?.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Senha deve ter pelo menos 8 caracteres';
    }
    
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    
    if (!formData?.agreeTerms) {
      newErrors.agreeTerms = 'Você deve aceitar os termos de uso';
    }
    
    if (!formData?.agreePrivacy) {
      newErrors.agreePrivacy = 'Você deve aceitar a política de privacidade';
    }
    
    return newErrors;
  };

  const handleNextStep = () => {
    const stepErrors = validateStep1();
    if (Object.keys(stepErrors)?.length > 0) {
      setErrors(stepErrors);
      return;
    }
    
    setErrors({});
    setCurrentStep(2);
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const stepErrors = validateStep2();
    if (Object.keys(stepErrors)?.length > 0) {
      setErrors(stepErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Usar função register real do AuthContext
      await register({
        email: formData.email,
        password: formData.password,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        agreeTerms: formData.agreeTerms,
        agreePrivacy: formData.agreePrivacy
      });
      
      // Se chegou aqui, registro foi bem-sucedido
      console.log('✅ Registro realizado com sucesso!');
      
      // Limpar erros antes de redirecionar
      setErrors({});
      
      // Redirecionar apenas após sucesso total
      navigate('/dashboard-home');
      
    } catch (error) {
      console.error('❌ Erro no registro:', error);
      
      // NÃO redirecionar em caso de erro
      setErrors({ 
        submit: error instanceof Error ? error.message : 'Erro ao criar conta. Tente novamente.' 
      });
      
      // Manter usuário na tela de registro
      console.log('⚠️ Usuário permanece na tela de registro devido ao erro');
      
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = formData?.password ? getPasswordStrength(formData?.password) : null;
  
  // Loading total considera loading local + auth loading
  const totalLoading = isLoading || authLoading;

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-caption text-muted-foreground">
            Passo {currentStep} de 2
          </span>
          <span className="text-sm font-caption text-muted-foreground">
            {currentStep === 1 ? 'Informações Pessoais' : 'Segurança'}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 2) * 100}%` }}
          />
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {currentStep === 1 && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Nome"
                type="text"
                name="firstName"
                value={formData?.firstName}
                onChange={handleInputChange}
                placeholder="Seu nome"
                error={errors?.firstName}
                required
              />
              <Input
                label="Sobrenome"
                type="text"
                name="lastName"
                value={formData?.lastName}
                onChange={handleInputChange}
                placeholder="Seu sobrenome"
                error={errors?.lastName}
                required
              />
            </div>

            <Input
              label="Email"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              placeholder="seu@email.com"
              description="Usaremos este email para comunicações importantes"
              error={errors?.email}
              required
            />

            <Button
              type="button"
              onClick={handleNextStep}
              variant="default"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
            >
              Continuar
            </Button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Input
              label="Senha"
              type="password"
              name="password"
              value={formData?.password}
              onChange={handleInputChange}
              placeholder="Crie uma senha forte"
              error={errors?.password}
              required
            />

            {/* Password Strength Indicator */}
            {formData?.password && passwordStrength && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-caption text-muted-foreground">
                    Força da senha:
                  </span>
                  <span className={`text-sm font-medium ${
                    passwordStrength?.label === 'Forte' ? 'text-success' :
                    passwordStrength?.label === 'Média' ? 'text-warning' : 'text-error'
                  }`}>
                    {passwordStrength?.label}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength?.color}`}
                    style={{ width: passwordStrength?.width }}
                  />
                </div>
              </div>
            )}

            <Input
              label="Confirmar Senha"
              type="password"
              name="confirmPassword"
              value={formData?.confirmPassword}
              onChange={handleInputChange}
              placeholder="Digite a senha novamente"
              error={errors?.confirmPassword}
              required
            />

            {/* Terms and Privacy */}
            <div className="space-y-4">
              <Checkbox
                label="Aceito os Termos de Uso"
                name="agreeTerms"
                checked={formData?.agreeTerms}
                onChange={handleInputChange}
                error={errors?.agreeTerms}
                description={
                  <span className="text-sm text-muted-foreground">
                    Li e concordo com os{' '}
                    <button 
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => window.open('/terms', '_blank')}
                    >
                      Termos de Uso
                    </button>
                  </span>
                }
              />

              <Checkbox
                label="Aceito a Política de Privacidade"
                name="agreePrivacy"
                checked={formData?.agreePrivacy}
                onChange={handleInputChange}
                error={errors?.agreePrivacy}
                description={
                  <span className="text-sm text-muted-foreground">
                    Li e concordo com a{' '}
                    <button 
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => window.open('/privacy', '_blank')}
                    >
                      Política de Privacidade
                    </button>
                  </span>
                }
              />
            </div>

            {errors?.submit && (
              <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            )}

            {/* Exibir erros do AuthContext */}
            {authError && (
              <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
                <p className="text-sm text-error">Erro de autenticação: {authError}</p>
              </div>
            )}

            <div className="flex space-x-4">
              <Button
                type="button"
                onClick={handlePrevStep}
                variant="outline"
                fullWidth
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Voltar
              </Button>
              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={totalLoading}
                iconName="UserPlus"
                iconPosition="right"
              >
                {totalLoading ? 'Criando...' : 'Criar Conta'}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default RegistrationForm;